"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SoftFiscalConfigV1 = exports.SoftFiscalApkSettings = exports.SoftFiscalApkFields = exports.SOFTFISCAL_DEFAULT_X8_PORT = exports.SOFTFISCAL_PRODUCTION_EKA_URL = exports.SOFTFISCAL_PRODUCTION_PKI_URL = exports.SoftFiscalHttpsUrl = exports.SoftFiscalPrinterModel = exports.SOFTFISCAL_PRINTER_MODELS = exports.SoftFiscalPrinterDevice = exports.SoftFiscalSmDevice = exports.SOFTFISCAL_CONFIG_SCHEMA_VERSION = void 0;
const zod_1 = require("zod");
/**
 * RL-957 — the configuration file BOS hands to SoftFiscal Android
 * (`store.reform.lite.softfiscal`), JSON format v1.
 *
 * ------------------------------------------------------------------
 * THIS SHAPE IS NOT OURS TO INVENT
 * ------------------------------------------------------------------
 * The format is UVS's (Tadas Norvaisas), `architektura/07_konfiguracijos_api.md`
 * v0.1 of 2026-10-21 in the SoftFiscal Android repo. Field names are the X8
 * parameter and SoftFiscal `app.config` names, so BOS and the POS team use
 * them without translation. The APK refuses an unknown field rather than
 * ignoring it, so `.strict()` throughout: a key BOS made up would fail the
 * whole file on the till.
 *
 * ------------------------------------------------------------------
 * WHAT ABSENCE MEANS — PER THE VENDOR'S CODE, NOT THE SPEC
 * ------------------------------------------------------------------
 * The spec (v0.1) says a missing section or field leaves the APK's value in
 * place. The APK itself (softfiscal-android @86e926a8, PC review 2026-10-22)
 * does not: `ApplyApk` REPLACES the whole apk.json, and `Settings` substitutes
 * its defaults for every omitted field. So a partial `apk` section would reset
 * the omitted values. A missing `apk` section is still left alone.
 *
 * Its X8 bridge also binds the constant port 8899: an `x8Port` is validated
 * and stored but not applied. BOS therefore only DELIVERS a complete section
 * (sm, printer, printerModel, pki, eka) with `x8Port` absent or 8899 — the
 * barrier is in the backend builder — until the vendor supports partial
 * sections and other ports.
 *
 * The `fiscal` section (the FB's VAT table, departments, payments, add-ons,
 * receipt header and footer — X8 SET) is not modelled yet: how BOS fills it is
 * an open owner question (RL-957 Q6). Until then a file with `fiscal` is
 * refused here, so BOS cannot emit one by accident.
 */
exports.SOFTFISCAL_CONFIG_SCHEMA_VERSION = 1;
/**
 * `usb-VVVV-PPPP[-SERIAL]`: USB vendor and product id, and a serial when two
 * identical chips are attached. The serial is 1..32 characters, as the
 * vendor's `UsbName`.
 */
const USB_DEVICE = /^usb-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}(-[A-Za-z0-9]{1,32})?$/;
/**
 * The security module. Android reaches it over USB only. A bare `usb` takes
 * the first FTDI device that is not the printer's port (spec 06).
 */
exports.SoftFiscalSmDevice = zod_1.z.string().refine((v) => v === 'usb' || USB_DEVICE.test(v), {
    message: 'must be usb or usb-VVVV-PPPP[-SERIAL]',
});
/** The receipt printer: a USB device, or `NUL` for none. Never a bare `usb`. */
exports.SoftFiscalPrinterDevice = zod_1.z.string().refine((v) => v === 'NUL' || USB_DEVICE.test(v), {
    message: 'must be usb-VVVV-PPPP[-SERIAL] or NUL',
});
/** The printer models UVS SoftFiscal drives (spec 06). */
exports.SOFTFISCAL_PRINTER_MODELS = [
    'TH210',
    'TH230',
    'TH230+',
    'TH230/0',
    'TH250',
    'P1200',
    'Z108',
    'TM-T20II',
    'TM-T20III',
    'TM-T20IV',
    'TM-T88',
    'TM-T88IV',
    'TM-T88V',
    'TM-T88VI',
    'TM-T88VII',
    'TM-m30III',
];
exports.SoftFiscalPrinterModel = zod_1.z.enum(exports.SOFTFISCAL_PRINTER_MODELS);
/**
 * An `https://` URL with a host. One non-throwing check rather than `.url()`
 * plus a refine: zod runs a refine even after `.url()` failed, and `new URL()`
 * on such a value throws, which would escape `safeParse` and the validation
 * pipe as a TypeError instead of a 400.
 */
exports.SoftFiscalHttpsUrl = zod_1.z.string().refine(isHttpsUrlWithHost, {
    message: 'must be an https:// URL',
});
function isHttpsUrlWithHost(value) {
    if (!value.startsWith('https://'))
        return false;
    try {
        return new URL(value).hostname.length > 0;
    }
    catch {
        return false;
    }
}
/**
 * The production PKI and i.EKA endpoints (owner answer Q5, 2026-10-22): the
 * values BOS's authoring form starts with. They are pre-fill only; a stored
 * block holds what the admin saved, and nothing is filled in silently.
 */
exports.SOFTFISCAL_PRODUCTION_PKI_URL = 'https://iekapki.uvs.lt';
exports.SOFTFISCAL_PRODUCTION_EKA_URL = 'https://ieka-cr-ws.vmi.lt';
/** The APK's X8 bridge port when none is set (`127.0.0.1:8899`). */
exports.SOFTFISCAL_DEFAULT_X8_PORT = 8899;
/**
 * The APK / device fields, each optional. Shared by the file's `apk` section
 * and BOS's `softFiscalAndroid` peripheral block, so both refuse the same
 * values.
 */
exports.SoftFiscalApkFields = zod_1.z
    .object({
    /** → `app.config` `sm`. */
    sm: exports.SoftFiscalSmDevice.optional(),
    /** → `app.config` `printer`. */
    printer: exports.SoftFiscalPrinterDevice.optional(),
    /** → `app.config` `printer_model`. */
    printerModel: exports.SoftFiscalPrinterModel.optional(),
    /** → `app.config` `pki`: the UVS PKI service. */
    pki: exports.SoftFiscalHttpsUrl.optional(),
    /** The i.EKA endpoint the transport module talks to. */
    eka: exports.SoftFiscalHttpsUrl.optional(),
    /** The APK's local X8 bridge port (the POS connects to 127.0.0.1:<port>). */
    x8Port: zod_1.z.number().int().min(1024).max(65535).optional(),
})
    .strict();
/**
 * Layer 2: the APK / device settings. Only the fields being changed are sent;
 * the section is omitted rather than sent empty.
 */
exports.SoftFiscalApkSettings = exports.SoftFiscalApkFields.refine((v) => Object.values(v).some((field) => field !== undefined), {
    message: 'an apk section needs at least one field; omit it instead',
});
/**
 * Only characters .NET `char.IsWhiteSpace` counts as white space — the test
 * behind the vendor's `string.IsNullOrWhiteSpace`. JS `trim()` differs: it
 * keeps NEXT LINE (U+0085) and strips U+FEFF, which .NET does not count.
 */
const DOTNET_WHITESPACE_ONLY = /^[\t\n\v\f\r \u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]*$/;
/** ISO 8601 date-time with an offset (`+03:00`) or `Z`. */
const ISO_DATE_TIME_WITH_OFFSET = zod_1.z.string().datetime({ offset: true });
exports.SoftFiscalConfigV1 = zod_1.z
    .object({
    schemaVersion: zod_1.z.literal(exports.SOFTFISCAL_CONFIG_SCHEMA_VERSION),
    /** BOS or hand-written version, for the APK's log and "Delivered to this till". */
    configVersion: zod_1.z
        .string()
        .min(1)
        .max(32)
        .refine((v) => !DOTNET_WHITESPACE_ONLY.test(v), { message: 'must not be blank' }),
    source: zod_1.z.enum(['manual', 'bos']),
    createdAt: ISO_DATE_TIME_WITH_OFFSET,
    apk: exports.SoftFiscalApkSettings.optional(),
})
    .strict();
//# sourceMappingURL=softfiscal-config.js.map